"use client";

/**
 * Platform URLs for the stage the page is ACTUALLY on.
 *
 * The shared chrome (logo, account menu, bell) used `NEXT_PUBLIC_*` values with
 * a prod fallback. Those are inlined at BUILD time, so any build made without
 * them (a static export, a build run outside the gateway) sent a local or dev
 * page's links and API calls to prod. On an installed PWA there is no address
 * bar to correct it from, so on local the notifications link opened prod's
 * settings page, which could never subscribe the local install.
 *
 * The page's own hostname is the one thing that cannot be wrong about which
 * stage it is on, so it decides. The env value is used only off-platform
 * (a host outside the base domain, e.g. a preview deploy), where the hostname
 * says nothing.
 */

import { useEffect, useState } from "react";

const BASE_DOMAIN = "gaz.codes";

/**
 * `apps.json` `environments[*].subdomain` for the non-prod stages. Prod has
 * none: its hosts sit straight under the base domain.
 */
const STAGE_LABELS = new Set(["l", "dev"]);

/**
 * The stage label of a platform host (`"l"`, `"dev"`, or `""` for prod), or
 * `null` for a host outside the platform.
 *
 * Mirrors `detectStageFromHostname` in `@workspace/stage-config`, which `ui`
 * does not depend on: the label is the one IMMEDIATELY left of the base domain,
 * matched whole. A substring test reads `model.gaz.codes` as local.
 */
function stageLabelOf(hostname: string): string | null {
  const host = hostname.toLowerCase().replace(/\.$/, "");
  if (host === BASE_DOMAIN) return "";
  if (!host.endsWith(`.${BASE_DOMAIN}`)) return null;
  const label = host.slice(0, -(BASE_DOMAIN.length + 1)).split(".").pop() ?? "";
  return STAGE_LABELS.has(label) ? label : "";
}

/**
 * The origin of the app at `subdomain` on `hostname`'s stage.
 * `undefined` means the apex app (web): `l.gaz.codes`, `gaz.codes`.
 *
 * `fallback` is returned for a host outside the platform.
 */
export function stageOriginFor(
  subdomain: string | undefined,
  hostname: string,
  fallback: string,
): string {
  const stage = stageLabelOf(hostname);
  if (stage === null) return fallback;
  return `https://${[subdomain, stage, BASE_DOMAIN].filter(Boolean).join(".")}`;
}

/**
 * As {@link stageOriginFor}, for the current page. Call it when the URL is
 * USED (an event handler, an effect, a fetch), never at module scope: there is
 * no `window` during SSR or a static export's prerender, and it returns
 * `fallback` there.
 */
export function currentStageOrigin(subdomain: string | undefined, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  return stageOriginFor(subdomain, window.location.hostname, fallback);
}

/**
 * {@link currentStageOrigin} for rendering a link. The first render returns
 * `fallback` on server and client alike (no hydration mismatch); the real
 * stage swaps in after mount.
 */
export function useStageOrigin(subdomain: string | undefined, fallback: string): string {
  const [origin, setOrigin] = useState(fallback);
  useEffect(() => {
    setOrigin(currentStageOrigin(subdomain, fallback));
  }, [subdomain, fallback]);
  return origin;
}
